import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format, parseISO, isAfter, isBefore, addDays, isValid, startOfDay, endOfDay } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';

const { FiClock, FiCalendar, FiInfo, FiChevronRight, FiChevronLeft, FiImage, FiRefreshCw, FiAlertTriangle } = FiIcons;

function ScheduleWidget() {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [corsError, setCorsError] = useState(false);
  const [currentDay, setCurrentDay] = useState(0); // 0=today, 1=tomorrow, etc.
  const [activeTab, setActiveTab] = useState('now');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastFetch, setLastFetch] = useState(null);
  const [currentShow, setCurrentShow] = useState(null);
  const [upcomingShows, setUpcomingShows] = useState([]);
  const [progress, setProgress] = useState(0);
  const progressTimerRef = useRef(null);
  const autoUpdateTimerRef = useRef(null);
  const itemsPerPage = 5;

  // NYC timezone - automatically handles EST/EDT
  const NYC_TIMEZONE = 'America/New_York';

  // Generate schedule for current week
  const generateCurrentWeekSchedule = () => {
    const now = getCurrentNYCTime();
    const currentWeekSchedule = [];
    
    // Generate schedule for 7 days starting from today
    for (let day = 0; day < 7; day++) {
      const targetDate = addDays(now, day);
      const dayOfWeek = targetDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
      
      // Base template programs for each day
      const basePrograms = [
        {
          name: 'BoroughTV Morning Show',
          start: '06:00',
          duration: 180, // 3 hours
          description: 'Start your day with NYC news, weather, and community highlights from all five boroughs.',
          icon: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752863923352-BTV-Logo.png'
        },
        {
          name: dayOfWeek === 0 ? 'Sunday Community Focus' : 
                dayOfWeek === 1 ? 'Community Spotlight' :
                dayOfWeek === 2 ? 'Arts & Culture NYC' :
                dayOfWeek === 3 ? 'Business Spotlight' :
                dayOfWeek === 4 ? 'Youth Voices' :
                dayOfWeek === 5 ? 'Weekend Preview' : 'Weekend Morning Show',
          start: '09:00',
          duration: dayOfWeek === 0 || dayOfWeek === 6 ? 120 : 90,
          description: dayOfWeek === 0 ? 'Community organizations and volunteer opportunities.' :
                      dayOfWeek === 1 ? 'Featuring local businesses and community leaders making a difference in NYC.' :
                      dayOfWeek === 2 ? 'Exploring NYC\'s vibrant arts scene, galleries, theaters, and cultural events.' :
                      dayOfWeek === 3 ? 'Featuring local entrepreneurs and small businesses across NYC.' :
                      dayOfWeek === 4 ? 'Highlighting young NYC creators, activists, and changemakers.' :
                      dayOfWeek === 5 ? 'What\'s happening this weekend across NYC - events, shows, and activities.' :
                      'Relaxed weekend programming with NYC lifestyle and entertainment.',
          icon: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752863923352-BTV-Logo.png'
        },
        {
          name: 'Lunch Live',
          start: '12:00',
          duration: 120,
          description: 'Midday talk show with interviews, music, and NYC lifestyle content.',
          icon: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752863923352-BTV-Logo.png'
        },
        {
          name: dayOfWeek === 0 ? 'Sunday Stories' :
                dayOfWeek === 1 ? 'Street Sounds' :
                dayOfWeek === 2 ? 'Neighborhood Walks' :
                dayOfWeek === 3 ? 'Wellness Wednesday' :
                dayOfWeek === 4 ? 'Throwback Thursday' :
                dayOfWeek === 5 ? 'Fashion Friday' :
                'Weekend Adventures',
          start: '14:00',
          duration: 120,
          description: dayOfWeek === 0 ? 'Long-form storytelling and documentary features about NYC life.' :
                      dayOfWeek === 1 ? 'Showcasing NYC\'s incredible music scene from subway performers to established artists.' :
                      dayOfWeek === 2 ? 'Virtual tours through NYC\'s diverse neighborhoods with local guides.' :
                      dayOfWeek === 3 ? 'Health, fitness, and wellness content featuring NYC experts and practitioners.' :
                      dayOfWeek === 4 ? 'Classic NYC content, historical footage, and nostalgic programming.' :
                      dayOfWeek === 5 ? 'NYC fashion, style, and design featuring local designers and trends.' :
                      'Exploring NYC\'s parks, attractions, and weekend activities.',
          icon: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752863923352-BTV-Logo.png'
        },
        {
          name: 'BoroughTV Evening News',
          start: '18:00',
          duration: 60,
          description: 'Local news and events from across the five boroughs.',
          icon: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752863923352-BTV-Logo.png'
        },
        {
          name: dayOfWeek === 0 ? 'Sunday Prime Time' :
                dayOfWeek === 1 ? 'Prime Time NYC' :
                dayOfWeek === 2 ? 'Live Music Tuesday' :
                dayOfWeek === 3 ? 'Documentary Night' :
                dayOfWeek === 4 ? 'Comedy Night NYC' :
                dayOfWeek === 5 ? 'Friday Night Live' :
                'Saturday Night Entertainment',
          start: '20:00',
          duration: dayOfWeek === 5 ? 180 : 120,
          description: dayOfWeek === 0 ? 'Special Sunday evening programming and featured content.' :
                      dayOfWeek === 1 ? 'Featured programming including documentaries and special events.' :
                      dayOfWeek === 2 ? 'Live performances from NYC artists across all genres.' :
                      dayOfWeek === 3 ? 'In-depth documentaries about NYC history, culture, and people.' :
                      dayOfWeek === 4 ? 'Stand-up comedy and comedy shows featuring NYC comedians.' :
                      dayOfWeek === 5 ? 'Special Friday night programming with live performances and events.' :
                      'Special weekend programming and entertainment.',
          icon: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752863923352-BTV-Logo.png'
        }
      ];

      // Convert base programs to full schedule entries
      basePrograms.forEach((program, index) => {
        const [hours, minutes] = program.start.split(':').map(Number);
        const startTime = new Date(targetDate);
        startTime.setHours(hours, minutes, 0, 0);
        
        const endTime = new Date(startTime);
        endTime.setMinutes(endTime.getMinutes() + program.duration);

        currentWeekSchedule.push({
          id: `${format(targetDate, 'yyyy-MM-dd')}-${program.name.toLowerCase().replace(/\s+/g, '-')}-${index}`,
          start_time: format(startTime, "yyyy-MM-dd'T'HH:mm:ssxxx", { timeZone: NYC_TIMEZONE }),
          end_time: format(endTime, "yyyy-MM-dd'T'HH:mm:ssxxx", { timeZone: NYC_TIMEZONE }),
          name: program.name,
          description: program.description,
          image: program.icon
        });
      });
    }

    return {
      channel: 'BoroughTV',
      programs: currentWeekSchedule.sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
    };
  };

  // Helper function to parse XMLTV data
  const parseXmltvData = (xmlData) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlData, "text/xml");
    
    const programs = [];
    const programElements = xmlDoc.getElementsByTagName("programme");
    
    for (let i = 0; i < programElements.length; i++) {
      const program = programElements[i];
      const title = program.getElementsByTagName("title")[0]?.textContent || "Untitled Program";
      const description = program.getElementsByTagName("desc")[0]?.textContent || "";
      const startTime = program.getAttribute("start");
      const endTime = program.getAttribute("stop");
      
      // Get icon/thumbnail URL if available
      const iconElement = program.getElementsByTagName("icon")[0];
      const iconSrc = iconElement?.getAttribute("src") || "";
      
      // Convert to ISO format (replacing the format from XMLTV)
      // Format is typically YYYYMMDDHHMMSS +0000
      const formatDateTime = (timeString) => {
        if (!timeString) return "";
        const year = timeString.substring(0, 4);
        const month = timeString.substring(4, 6);
        const day = timeString.substring(6, 8);
        const hour = timeString.substring(8, 10);
        const minute = timeString.substring(10, 12);
        const second = timeString.substring(12, 14);
        const timezone = timeString.substring(15);
        
        return `${year}-${month}-${day}T${hour}:${minute}:${second}${timezone}`;
      };
      
      programs.push({
        id: `program-${i}`,
        name: title,
        description: description,
        start_time: formatDateTime(startTime),
        end_time: formatDateTime(endTime),
        image: iconSrc || 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752863923352-BTV-Logo.png'
      });
    }
    
    return {
      channel: 'BoroughTV',
      programs: programs
    };
  };

  // Initial data fetch
  useEffect(() => {
    // Try to fetch the real schedule data
    const fetchSchedule = async () => {
      setLoading(true);
      setCorsError(false);
      
      try {
        // Add cache busting parameter to prevent caching issues
        const cacheBuster = new Date().getTime();
        const response = await fetch(`https://deliver.viloud.tv/channel/651df68dee0117592d2f77ccfbb049dd/epg?format=xmltv&days_backward=3&days_forward=7&_cb=${cacheBuster}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/xml',
            'Origin': window.location.origin
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch schedule: ${response.status} ${response.statusText}`);
        }
        
        const xmlData = await response.text();
        console.log("XML Data received:", xmlData.substring(0, 500) + "..."); // Log a snippet of XML
        const parsedData = parseXmltvData(xmlData);
        setSchedule(parsedData);
        setLastFetch(new Date());
        setError(null);
        console.log("Schedule loaded from API:", parsedData.programs.length, "programs");
        
        // Log the first few programs to check for image URLs
        if (parsedData.programs.length > 0) {
          console.log("Sample programs with images:", 
            parsedData.programs.slice(0, 3).map(p => ({
              name: p.name,
              image: p.image
            }))
          );
        }
      } catch (err) {
        console.error("Schedule fetch error:", err);
        
        // Check if it's likely a CORS error
        if (err.message.includes('NetworkError') || 
            err.message.includes('Failed to fetch') || 
            err.message.includes('CORS')) {
          console.error("CORS error detected, falling back to static schedule");
          setCorsError(true);
          
          // Fall back to static schedule
          try {
            const weeklySchedule = generateCurrentWeekSchedule();
            setSchedule(weeklySchedule);
            setLastFetch(new Date());
            setError("Unable to fetch schedule from API due to CORS restrictions. Using static schedule instead.");
            console.log("Static schedule loaded:", weeklySchedule.programs.length, "programs");
          } catch (staticErr) {
            console.error("Static schedule generation error:", staticErr);
            setError("Failed to generate schedule");
          }
        } else {
          setError("Failed to fetch schedule");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
    
    // Clean up any running timers when component unmounts
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
      if (autoUpdateTimerRef.current) {
        clearInterval(autoUpdateTimerRef.current);
      }
    };
  }, []);

  // Reset pagination when changing days
  useEffect(() => {
    setCurrentPage(1);
  }, [currentDay]);

  // Update shows and progress automatically
  useEffect(() => {
    if (!schedule) return;
    
    // Initial update of current and upcoming shows
    updateCurrentAndUpcomingShows();
    
    // Set up auto-update timer - check every minute
    autoUpdateTimerRef.current = setInterval(() => {
      updateCurrentAndUpcomingShows();
    }, 60000); // 60 seconds
    
    // Set up progress bar timer - update every second
    progressTimerRef.current = setInterval(() => {
      if (currentShow) {
        setProgress(calculateProgress(currentShow));
      }
    }, 1000); // 1 second
    
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
      if (autoUpdateTimerRef.current) {
        clearInterval(autoUpdateTimerRef.current);
      }
    };
  }, [schedule]);
  
  // Update current and upcoming shows
  const updateCurrentAndUpcomingShows = () => {
    if (!schedule) return;
    
    const newCurrentShow = getCurrentShow(schedule);
    const newUpcomingShows = getUpcomingShows(schedule, 2);
    
    // Only update state if something has changed to prevent unnecessary re-renders
    if (JSON.stringify(newCurrentShow) !== JSON.stringify(currentShow)) {
      setCurrentShow(newCurrentShow);
      console.log("Current show updated:", newCurrentShow?.name);
    }
    
    if (JSON.stringify(newUpcomingShows) !== JSON.stringify(upcomingShows)) {
      setUpcomingShows(newUpcomingShows);
      console.log("Upcoming shows updated, count:", newUpcomingShows.length);
    }
    
    // Always update progress
    if (newCurrentShow) {
      setProgress(calculateProgress(newCurrentShow));
    }
  };

  // Safe parsing of ISO date strings with NYC timezone support
  const safeParseISO = (dateString) => {
    try {
      if (!dateString) return null;
      const parsedDate = parseISO(dateString);
      return isValid(parsedDate) ? parsedDate : null;
    } catch (e) {
      console.error("Date parsing error:", e, dateString);
      return null;
    }
  };

  // Get current NYC time for comparisons
  const getCurrentNYCTime = () => {
    return utcToZonedTime(new Date(), NYC_TIMEZONE);
  };

  // Get current timezone abbreviation (EST/EDT)
  const getCurrentTimezoneAbbr = () => {
    const now = new Date();
    const timeZoneName = now.toLocaleString('en-US', {
      timeZone: NYC_TIMEZONE,
      timeZoneName: 'short'
    }).split(' ').pop();
    return timeZoneName;
  };

  const getCurrentShow = (schedule) => {
    if (!schedule || !schedule.programs || !Array.isArray(schedule.programs)) {
      console.log("Invalid schedule data for current show");
      return null;
    }

    const now = getCurrentNYCTime();
    for (const program of schedule.programs) {
      if (!program.start_time || !program.end_time) continue;
      
      const startTime = safeParseISO(program.start_time);
      const endTime = safeParseISO(program.end_time);
      
      if (!startTime || !endTime) continue;
      
      if (isAfter(now, startTime) && isBefore(now, endTime)) {
        return program;
      }
    }
    return null;
  };

  const getUpcomingShows = (schedule, count = 2) => {
    if (!schedule || !schedule.programs || !Array.isArray(schedule.programs)) {
      console.log("Invalid schedule data for upcoming shows");
      return [];
    }

    const now = getCurrentNYCTime();
    const upcoming = schedule.programs
      .filter(program => {
        if (!program.start_time) return false;
        const startTime = safeParseISO(program.start_time);
        return startTime && isAfter(startTime, now);
      })
      .sort((a, b) => {
        const aTime = safeParseISO(a.start_time);
        const bTime = safeParseISO(b.start_time);
        if (!aTime || !bTime) return 0;
        return aTime - bTime;
      })
      .slice(0, count);

    return upcoming;
  };

  const getDaySchedule = (schedule, dayOffset = 0) => {
    if (!schedule || !schedule.programs || !Array.isArray(schedule.programs)) {
      console.log("Invalid schedule data for day schedule");
      return [];
    }

    // Get target date in NYC timezone
    const nycNow = getCurrentNYCTime();
    const targetDate = addDays(nycNow, dayOffset);
    const targetDateString = format(targetDate, 'yyyy-MM-dd');

    return schedule.programs
      .filter(program => {
        if (!program.start_time) return false;
        const programDate = safeParseISO(program.start_time);
        return programDate && format(programDate, 'yyyy-MM-dd') === targetDateString;
      })
      .sort((a, b) => {
        const aTime = safeParseISO(a.start_time);
        const bTime = safeParseISO(b.start_time);
        if (!aTime || !bTime) return 0;
        return aTime - bTime;
      });
  };

  const formatTime = (timeString) => {
    try {
      const date = safeParseISO(timeString);
      if (!date) return 'Invalid time';
      // Format in NYC timezone
      return format(date, 'h:mm a', { timeZone: NYC_TIMEZONE });
    } catch (e) {
      console.error("Time formatting error:", e);
      return 'Invalid time';
    }
  };

  const calculateProgress = (current) => {
    if (!current || !current.start_time || !current.end_time) return 0;
    
    const now = getCurrentNYCTime();
    const start = safeParseISO(current.start_time);
    const end = safeParseISO(current.end_time);
    
    if (!start || !end) return 0;
    
    const totalDuration = end - start;
    if (totalDuration <= 0) return 0;
    
    const elapsed = now - start;
    return Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100);
  };

  const changeDay = (increment) => {
    setCurrentDay(prev => {
      const newDay = prev + increment;
      // Limit to 6 days ahead
      return Math.min(Math.max(newDay, 0), 6);
    });
    setActiveTab('daily');
  };

  // Pagination functions
  const getPaginatedSchedule = (fullSchedule) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return fullSchedule.slice(startIndex, startIndex + itemsPerPage);
  };

  const totalPages = (fullSchedule) => {
    return Math.ceil(fullSchedule.length / itemsPerPage);
  };

  const nextPage = () => {
    const daySchedule = getDaySchedule(schedule, currentDay);
    const maxPage = totalPages(daySchedule);
    if (currentPage < maxPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Refresh schedule data
  const refreshSchedule = () => {
    setLoading(true);
    setCorsError(false);
    
    // Try to fetch the real schedule data again
    const fetchSchedule = async () => {
      try {
        // Add cache busting parameter to prevent caching issues
        const cacheBuster = new Date().getTime();
        const response = await fetch(`https://deliver.viloud.tv/channel/651df68dee0117592d2f77ccfbb049dd/epg?format=xmltv&days_backward=3&days_forward=7&_cb=${cacheBuster}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/xml',
            'Origin': window.location.origin
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch schedule: ${response.status} ${response.statusText}`);
        }
        
        const xmlData = await response.text();
        const parsedData = parseXmltvData(xmlData);
        setSchedule(parsedData);
        setLastFetch(new Date());
        setError(null);
        console.log("Schedule refreshed from API:", parsedData.programs.length, "programs");
      } catch (err) {
        console.error("Schedule refresh error:", err);
        
        // Check if it's likely a CORS error
        if (err.message.includes('NetworkError') || 
            err.message.includes('Failed to fetch') || 
            err.message.includes('CORS')) {
          console.error("CORS error detected, falling back to static schedule");
          setCorsError(true);
          
          // Fall back to static schedule
          try {
            const weeklySchedule = generateCurrentWeekSchedule();
            setSchedule(weeklySchedule);
            setLastFetch(new Date());
            setError("Unable to fetch schedule from API due to CORS restrictions. Using static schedule instead.");
            console.log("Static schedule refreshed:", weeklySchedule.programs.length, "programs");
          } catch (staticErr) {
            console.error("Static schedule generation error:", staticErr);
            setError("Failed to generate schedule");
          }
        } else {
          setError("Failed to fetch schedule");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  };

  // Get current timezone abbreviation for display
  const timezoneAbbr = getCurrentTimezoneAbbr();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  // Check if we have valid schedule data
  if (!schedule || !schedule.programs || !Array.isArray(schedule.programs) || schedule.programs.length === 0) {
    return (
      <div className="bg-dark-card rounded-lg border border-dark-border overflow-hidden">
        <div className="flex border-b border-dark-border">
          <button
            onClick={() => setActiveTab('now')}
            className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
              activeTab === 'now'
                ? 'text-accent-blue border-b-2 border-accent-blue'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <SafeIcon icon={FiClock} />
              <span>Now & Next</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('daily')}
            className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
              activeTab === 'daily'
                ? 'text-accent-blue border-b-2 border-accent-blue'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <SafeIcon icon={FiCalendar} />
              <span>Daily Schedule</span>
            </div>
          </button>
        </div>

        <div className="text-center text-gray-400 p-10">
          <SafeIcon icon={FiInfo} className="text-3xl mx-auto mb-2" />
          <p>No schedule information available</p>
          <p className="text-sm mt-2">Please check back later for updated programming</p>
          <button
            onClick={refreshSchedule}
            className="mt-4 bg-accent-blue hover:bg-accent-blue/80 text-white px-4 py-2 rounded-md transition-colors"
          >
            Refresh Schedule
          </button>
        </div>
      </div>
    );
  }

  const fullDaySchedule = getDaySchedule(schedule, currentDay);
  const paginatedDaySchedule = getPaginatedSchedule(fullDaySchedule);

  const nycNow = getCurrentNYCTime();
  const dayLabel = currentDay === 0 ? 'Today' : 
                   currentDay === 1 ? 'Tomorrow' : 
                   format(addDays(nycNow, currentDay), 'EEEE');
  const maxPages = totalPages(fullDaySchedule);

  return (
    <div className="bg-dark-card rounded-lg border border-dark-border overflow-hidden">
      {/* Header with refresh indicator */}
      <div className="flex border-b border-dark-border">
        <button
          onClick={() => setActiveTab('now')}
          className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
            activeTab === 'now'
              ? 'text-accent-blue border-b-2 border-accent-blue'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <SafeIcon icon={FiClock} />
            <span>Now & Next</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('daily')}
          className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
            activeTab === 'daily'
              ? 'text-accent-blue border-b-2 border-accent-blue'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <SafeIcon icon={FiCalendar} />
            <span>Daily Schedule</span>
          </div>
        </button>
      </div>

      {/* CORS Error Notice */}
      {corsError && (
        <div className="px-4 py-3 bg-yellow-900/30 border-b border-yellow-700/50">
          <div className="flex items-start space-x-2">
            <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-200">
              <p>We're having trouble loading the live schedule due to technical restrictions.</p>
              <p className="mt-1 text-yellow-400/80">Our team has been notified and is working on a solution. Showing estimated programming in the meantime.</p>
            </div>
          </div>
        </div>
      )}

      {/* Last update indicator */}
      {lastFetch && (
        <div className="px-4 py-2 bg-dark-bg border-b border-dark-border">
          <div className="flex justify-between items-center text-xs text-gray-400">
            <span>Schedule updated: {format(lastFetch, 'h:mm:ss a')} {timezoneAbbr}</span>
            <button
              onClick={refreshSchedule}
              className="text-accent-blue hover:text-accent-blue/80 transition-colors flex items-center space-x-1"
            >
              <SafeIcon icon={FiRefreshCw} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      )}

      {activeTab === 'now' ? (
        <div className="p-6">
          {/* Currently Airing */}
          {currentShow ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-8"
              key={`current-${currentShow.id}`}
            >
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <h3 className="text-xl font-semibold">On Air Now</h3>
                <span className="text-xs text-gray-400">({timezoneAbbr})</span>
              </div>
              <div className="bg-dark-bg p-6 rounded-lg border border-dark-border">
                <div className="flex flex-col md:flex-row gap-4">
                  {currentShow.image && (
                    <div className="w-full md:w-40 flex-shrink-0">
                      <img
                        src={currentShow.image}
                        alt={currentShow.name}
                        className="w-full h-auto rounded-lg object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752863923352-BTV-Logo.png';
                        }}
                      />
                    </div>
                  )}
                  <div className="flex-grow">
                    <h4 className="text-2xl font-bold mb-2">{currentShow.name}</h4>
                    <div className="flex items-center text-gray-400 mb-4">
                      <span>{formatTime(currentShow.start_time)} - {formatTime(currentShow.end_time)} {timezoneAbbr}</span>
                    </div>
                    {currentShow.description && (
                      <p className="text-gray-300 mb-4">{currentShow.description}</p>
                    )}
                    <div className="w-full bg-dark-border rounded-full h-2 mb-2">
                      <div
                        className="bg-accent-blue h-full rounded-full transition-all duration-1000 ease-linear"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>{formatTime(currentShow.start_time)}</span>
                      <span>{formatTime(currentShow.end_time)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-8"
            >
              <div className="flex items-center space-x-2 mb-4">
                <h3 className="text-xl font-semibold">Currently Off Air</h3>
                <span className="text-xs text-gray-400">({timezoneAbbr})</span>
              </div>
              <div className="bg-dark-bg p-6 rounded-lg border border-dark-border text-center">
                <p className="text-gray-400">No show is currently broadcasting.</p>
                <p className="text-gray-400 mt-2">Check the upcoming schedule below.</p>
              </div>
            </motion.div>
          )}

          {/* Coming Up Next */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <h3 className="text-xl font-semibold mb-4">Coming Up Next</h3>
            {upcomingShows && upcomingShows.length > 0 ? (
              <div className="space-y-4">
                {upcomingShows.map((show, index) => (
                  <div
                    key={`upcoming-${show.id}-${index}`}
                    className="bg-dark-bg p-4 rounded-lg border border-dark-border hover:border-accent-blue/50 transition-colors"
                  >
                    <div className="flex gap-3">
                      {show.image && (
                        <div className="w-24 h-16 flex-shrink-0">
                          <img
                            src={show.image}
                            alt={show.name}
                            className="w-full h-full rounded-md object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752863923352-BTV-Logo.png';
                            }}
                          />
                        </div>
                      )}
                      <div className="flex-grow flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold mb-1">{show.name}</h4>
                          <div className="text-gray-400 text-sm">
                            {formatTime(show.start_time)} - {formatTime(show.end_time)} {timezoneAbbr}
                          </div>
                        </div>
                        <div className="bg-dark-card px-3 py-1 rounded-full text-xs text-gray-400">
                          {show.start_time ? format(safeParseISO(show.start_time), 'EEE') : 'Unknown'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 p-6">
                No upcoming shows scheduled
              </div>
            )}
          </motion.div>
        </div>
      ) : (
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => changeDay(-1)}
              disabled={currentDay === 0}
              className={`p-2 rounded-full ${
                currentDay === 0
                  ? 'text-gray-600 cursor-not-allowed'
                  : 'text-gray-400 hover:text-white hover:bg-dark-bg'
              }`}
            >
              <SafeIcon icon={FiChevronLeft} className="text-xl" />
            </button>
            <div className="text-center">
              <h3 className="text-xl font-semibold">{dayLabel}</h3>
              <span className="text-xs text-gray-400">Eastern Time</span>
            </div>
            <button
              onClick={() => changeDay(1)}
              disabled={currentDay === 6}
              className={`p-2 rounded-full ${
                currentDay === 6
                  ? 'text-gray-600 cursor-not-allowed'
                  : 'text-gray-400 hover:text-white hover:bg-dark-bg'
              }`}
            >
              <SafeIcon icon={FiChevronRight} className="text-xl" />
            </button>
          </div>

          {fullDaySchedule && fullDaySchedule.length > 0 ? (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="space-y-4"
                key={`day-${currentDay}-page-${currentPage}`}
              >
                {paginatedDaySchedule.map((show, index) => {
                  const isNow = currentDay === 0 && currentShow && show.id === currentShow.id;
                  return (
                    <div
                      key={`day-${show.id}-${index}`}
                      className={`bg-dark-bg p-4 rounded-lg border transition-colors ${
                        isNow
                          ? 'border-accent-blue'
                          : 'border-dark-border hover:border-accent-blue/50'
                      }`}
                    >
                      <div className="flex gap-3">
                        {show.image && (
                          <div className="w-24 h-16 flex-shrink-0">
                            <img
                              src={show.image}
                              alt={show.name}
                              className="w-full h-full rounded-md object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752863923352-BTV-Logo.png';
                              }}
                            />
                          </div>
                        )}
                        <div className="flex-grow">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className="font-semibold">{show.name}</h4>
                                {isNow && (
                                  <div className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">Live</div>
                                )}
                              </div>
                              <div className="text-gray-400 text-sm mt-1">
                                {formatTime(show.start_time)} - {formatTime(show.end_time)} {timezoneAbbr}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-gray-400">
                                {show.start_time ? format(safeParseISO(show.start_time), 'h:mm a') : 'Unknown time'}
                              </div>
                            </div>
                          </div>
                          {show.description && (
                            <div className="mt-2 text-sm text-gray-400">{show.description}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </motion.div>

              {/* Pagination Controls */}
              {maxPages > 1 && (
                <div className="mt-6 flex justify-center">
                  <nav className="flex items-center space-x-2" aria-label="Pagination">
                    <button
                      onClick={prevPage}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-md ${
                        currentPage === 1
                          ? 'text-gray-600 cursor-not-allowed'
                          : 'text-gray-400 hover:text-white hover:bg-dark-bg'
                      }`}
                      aria-label="Previous page"
                    >
                      <SafeIcon icon={FiChevronLeft} className="text-lg" />
                    </button>
                    <span className="text-sm text-gray-400">
                      Page {currentPage} of {maxPages}
                    </span>
                    <button
                      onClick={nextPage}
                      disabled={currentPage === maxPages}
                      className={`p-2 rounded-md ${
                        currentPage === maxPages
                          ? 'text-gray-600 cursor-not-allowed'
                          : 'text-gray-400 hover:text-white hover:bg-dark-bg'
                      }`}
                      aria-label="Next page"
                    >
                      <SafeIcon icon={FiChevronRight} className="text-lg" />
                    </button>
                  </nav>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-gray-400 p-10">
              <SafeIcon icon={FiInfo} className="text-3xl mx-auto mb-2" />
              <p>No programs scheduled for this day</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ScheduleWidget;